import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, concatMap, finalize, Observable, of, ReplaySubject } from 'rxjs';

import { GamlAttestationService } from '../../services/gaml-attestation/gaml-attestation.service';
import { PreferencesService } from '../../services/preferences/preferences.service';
import { W8BenFormInitService } from './w8ben/w8ben-form-init.service';
import { W8benGlobalNotificationBannerComponent } from './w8ben/w8ben-global-notification-banner.component';
import { GamlGlobalNotificationBannerComponent } from './gaml/gaml-global-notification-banner.component';
import { GamlHaltTradeGlobalNotificationBannerComponent } from './gaml/halt-trade/gaml-halt-trade-global-notification-banner.component';
import { GlobalNotificationBannerService } from './global-notification-banner.service';

@Injectable({
  providedIn: 'root'
})
export class InitGlobalNotificationBannerService {
  private readonly globalNotificationBannerService = inject(GlobalNotificationBannerService);
  private readonly w8benFormInitService = inject(W8BenFormInitService);
  private readonly gamlAttestationService = inject(GamlAttestationService);
  private readonly preferencesService = inject(PreferencesService);
  private readonly destroyRef = inject(DestroyRef);

  readonly GAML_GLOBAL_BANNER_ID: string = 'gaml-global-notification-banner';
  readonly GAML_GLOBAL_BANNER_HALT_TRADE_ID: string = 'gaml-halt-trade-global-notification-banner';
  readonly W8BEN_GLOBAL_BANNER_ID: string = 'w8ben-global-notification-banner';
  gamlGlobalBannerAttestation$: Observable<boolean>;

  constructor() {
    this.gamlGlobalBannerAttestation$ = toObservable(this.gamlAttestationService.attestationComplete);
  }

  init(): void {
    this.globalNotificationBannerService.init();

    of(true)
      .pipe(
        concatMap(() => this.displayGamlGlobalBanner()),
        concatMap(() => this.displayW8benGlobalBanner())
      )
      .subscribe();
  }

  private displayGamlGlobalBanner(): Observable<boolean> {
    const gaml$ = new ReplaySubject<boolean>();

    this.gamlAttestationService
      .getStatusData()
      .pipe(
        finalize(() => {
          gaml$.next(true);
          gaml$.complete();
        })
      )
      .subscribe(() => {
        this.onGamlGlobalBannerRegistration();
        this.onGamlGlobalBannerRemove();
      });

    return gaml$;
  }

  private displayW8benGlobalBanner(): Observable<boolean> {
    const w8ben$ = new ReplaySubject<boolean>();

    if(this.w8benFormInitService.w8benEnabled) {
      this.globalNotificationBannerService.registerBanner({
        id: this.W8BEN_GLOBAL_BANNER_ID,
        component: W8benGlobalNotificationBannerComponent,
        showOnHomePageOnly: false
      });

      this.preferencesService.onPreferenceValueChange
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (): void => {
            if(!this.w8benFormInitService.w8benEnabled) {
              this.globalNotificationBannerService.removeBanner(this.W8BEN_GLOBAL_BANNER_ID);
            }
          }
        });
    }
    w8ben$.next(true);
    w8ben$.complete();
    return w8ben$;
  }

  private onGamlGlobalBannerRegistration(): void {
    combineLatest([
      this.gamlAttestationService.gamlTriggered$,
      this.gamlAttestationService.gamlHaltTrade$
    ]).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([gamlTriggered, gamlHaltTrade]: [boolean, boolean]) => {
        if (gamlTriggered || gamlHaltTrade) {
          const gamlGlobalBanner = gamlTriggered ?
            {
              id: this.GAML_GLOBAL_BANNER_ID,
              component: GamlGlobalNotificationBannerComponent,
              showOnHomePageOnly: false
            } :
            {
              id: this.GAML_GLOBAL_BANNER_HALT_TRADE_ID,
              component: GamlHaltTradeGlobalNotificationBannerComponent,
              showOnHomePageOnly: false
            }

          if (!this.globalNotificationBannerService.isBannerDisplayed(gamlGlobalBanner.id)) {
            this.globalNotificationBannerService.registerBanner(gamlGlobalBanner);
          }
        }
      });
  }

  private onGamlGlobalBannerRemove(): void {
    this.gamlGlobalBannerAttestation$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((attestation: boolean) => {
        if(attestation) {
          this.globalNotificationBannerService.removeBanner(this.GAML_GLOBAL_BANNER_ID);
          this.globalNotificationBannerService.removeBanner(this.GAML_GLOBAL_BANNER_HALT_TRADE_ID);
        }
      });
  }
}
